import type { CSSProperties, ReactNode } from 'react';
import { Card, Container, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { dashboardService } from '../services/dashboard.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import SampleDashboardQuickAccess from '../components/samples/SampleDashboardQuickAccess';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import { navigateToSampleDetail } from '../utils/samplePageNavigation';
import { SAMPLE_STORAGE_SETUP_HREF } from '../utils/sampleStorageSetupGate';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { reportService } from '../services/report.service';
import { joinLabeledValue } from '../utils/joinLabeledValue';
import { formatDashboardActivity } from '../utils/sampleDashboardActivity';
import {
  buildDistributionBarChartOptions,
  buildSampleDashboardChartOptions,
  distributionChartHeightPx,
  formatRegistrationChartAxisLabel,
  registrationChartMaxTicksLimit,
} from '../utils/sampleDashboardChart';
import { mergePathogenGenusBars } from '../utils/pathogenGenusDisplay';
import { sortBslCountBars } from '../utils/sampleBslDisplay';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { SamplePanelCardHeader } from '../components/samples/SamplePanelCardHeader';
import styles from './SampleDashboard.module.css';

/** ダッシュボード「最近のアクティビティ」に出す件数（一覧感を出さない） */
const DASHBOARD_ACTIVITY_PREVIEW = 8;

/** チャートキャンバスに名前を付ける（Chart.js の canvas は既定で無名） */
function ChartFigure({
  label,
  className,
  style,
  children,
}: {
  label: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div className={className} style={style} role="img" aria-label={label}>
      {/* Chart.js canvas も role=img になるため、内側は装飾扱いにする */}
      <div aria-hidden="true" className="h-100">
        {children}
      </div>
    </div>
  );
}

/** 件数棒グラフの共通 dataset（属別／BSL で重複させない） */
function buildCountBarDataset(label: string, data: number[], barCount: number) {
  const few = barCount > 0 && barCount <= 5;
  return {
    label,
    data,
    backgroundColor: 'rgba(37, 99, 235, 0.8)',
    borderColor: '#2563eb',
    borderWidth: 2,
    maxBarThickness: few ? 40 : 56,
    /** 件数差が大きいとき極小バーが消えないよう最低ピクセル長を確保 */
    minBarLength: 4,
    categoryPercentage: few ? 0.72 : 0.55,
    barPercentage: few ? 0.85 : 0.8,
  };
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type DashboardChromeProps = {
  shortLabel: string;
  fullLabel: string;
  description: string;
};

function DashboardStatCard({
  label,
  value,
  iconClassName,
  toneClassName,
  to,
  linkTitle,
}: {
  label: string;
  value: number;
  iconClassName: string;
  toneClassName: string;
  /** 指定時は一覧／履歴などへ遷移できるカードにする */
  to?: string;
  /** title = 遷移の長語。aria は表示ラベル＋件数と一致させる */
  linkTitle?: string;
}) {
  const { t } = useTranslation();
  const card = (
    <Card
      className={`h-100 text-white border-0 shadow-sm ${styles.statCard} ${toneClassName}${
        to ? ` ${styles.statCardLinkInner}` : ''
      }`}
    >
      <Card.Body className={`py-1 px-2 px-md-3 ${styles.statBody}`}>
        <div className={`d-flex align-items-center justify-content-between gap-2 ${styles.statRow}`}>
          <div className={`min-w-0 ${styles.statText}`}>
            <span className={styles.statLabel}>{label}</span>
            <span className="visually-hidden" aria-hidden="true">{' · '}</span>
            <span className={`fw-bold ${styles.statValue}`}>{value}</span>
          </div>
          <i className={`fas ${iconClassName} ${styles.statIcon}`} aria-hidden="true" />
        </div>
      </Card.Body>
    </Card>
  );

  if (!to) return card;

  // visible（ラベル＋数値）と aria を揃える。title だけ遷移の長語
  const linkAria = t('dashboard.stats_card_link_aria', {
    defaultValue: '{{label}} · {{count}}',
    label,
    count: value,
  });

  return (
    <Link to={to} className={styles.statCardLink} aria-label={linkAria} title={linkTitle}>
      {card}
    </Link>
  );
}

function DistributionBarCard({
  title,
  icon,
  labels,
  values,
  countLabel,
  options,
  chartKey,
}: {
  title: string;
  icon: string;
  labels: string[];
  values: number[];
  countLabel: string;
  options: Record<string, unknown>;
  chartKey: string;
}) {
  const horizontal = options.indexAxis === 'y';
  const height = distributionChartHeightPx(labels.length, horizontal);
  return (
    <Card className={`h-100 border-0 shadow-sm sample-panel-card ${styles.panelCard}`}>
      <SamplePanelCardHeader icon={icon} title={title} />
      <Card.Body className="p-2 p-md-3">
        <ChartFigure label={title} className={styles.chartWrapSm} style={{ height }}>
          <Bar
            key={chartKey}
            data={{
              labels,
              datasets: [buildCountBarDataset(countLabel, values, labels.length)],
            }}
            options={options as any}
          />
        </ChartFigure>
      </Card.Body>
    </Card>
  );
}

function DashboardChrome({ shortLabel, fullLabel, description }: DashboardChromeProps) {
  return (
    <>
      <SampleManagerNavigation />
      {/* ナビ短名「概要」と可視 h1 が縦を奪うため sr-only。管理者トグルは統計グリッド横へ */}
      <SampleManagerPageHeading shortLabel={shortLabel} fullLabel={fullLabel} srOnly />
      <p className={`visually-hidden ${styles.pageDesc}`}>{description}</p>
    </>
  );
}

export default function SampleDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);

  const shortLabel = t('sample.dashboard.nav_short', { defaultValue: 'Overview' });
  const fullLabel = t('sample.dashboard.title', { defaultValue: 'Overview' });
  const description = t('sample.dashboard.description', {
    defaultValue: 'View statistics and activity for the sample management system',
  });

  const chromeProps: DashboardChromeProps = {
    shortLabel,
    fullLabel,
    description,
  };

  const {
    data: stats,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['dashboard', 'stats', adminViewAll],
    queryFn: () => dashboardService.getStats({ adminViewAll: adminViewAll || undefined }),
    staleTime: 1000 * 60 * 5,
    /** スコープ切替で全画面スピナーに飛ばない（直前の統計を保持） */
    placeholderData: (prev) => prev,
  });

  const { data: distributionData } = useQuery({
    queryKey: ['distribution-analysis', adminViewAll],
    queryFn: () =>
      reportService.getDistributionAnalysis({ adminViewAll: adminViewAll || undefined }),
    placeholderData: (prev) => prev,
  });

  /* 初回のみ全画面。スコープ切替は帯を残したまま isFetching で薄くする */
  if (isLoading && !stats) {
    return (
      <Container fluid id="sample-dashboard-page" className={`mt-2 px-3 px-md-4 pb-3 ${styles.page}`}>
        <DashboardChrome {...chromeProps} />
        <LoadingSpinner
          className="mt-5"
          text={t('dashboard.loading_stats', { defaultValue: 'Loading statistics...' })}
        />
      </Container>
    );
  }

  if (isError) {
    const rawErrorMessage = error instanceof Error ? error.message : t('common.error_occurred');
    const errorMessage = rawErrorMessage.startsWith('api.error.')
      ? t(rawErrorMessage, { defaultValue: rawErrorMessage })
      : rawErrorMessage;

    return (
      <Container fluid id="sample-dashboard-page" className={`mt-2 px-3 px-md-4 pb-3 ${styles.page}`}>
        <DashboardChrome {...chromeProps} />
        <ErrorAlert
          message={errorMessage}
          showRetry={true}
          onRetry={() => {
            void queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
            void queryClient.invalidateQueries({ queryKey: ['distribution-analysis'] });
          }}
        />
      </Container>
    );
  }

  /** API 順が年月バラバラだと誤った増減に見えるため YYYY-MM で整列 */
  const chartPoints = [...(stats?.chart_data ?? [])].sort((a, b) =>
    String(a.month || '').localeCompare(String(b.month || ''))
  );
  /** 実登録が1点以下なら要約（ゼロ埋め月で「急減」に見せない） */
  const nonzeroChartPoints = chartPoints.filter((item) => (item.count ?? 0) > 0);
  const useSparseRegistrationChart = nonzeroChartPoints.length <= 1;
  const sparseChartPoint = nonzeroChartPoints[0] ?? chartPoints[0];
  const chartSpansYears =
    new Set(chartPoints.map((item) => String(item.month || '').slice(0, 4)).filter(Boolean))
      .size > 1;
  const chartLabelCount = chartPoints.length;
  const chartData =
    chartPoints.length > 0
      ? {
          labels: chartPoints.map((item) =>
            formatRegistrationChartAxisLabel(item.month, chartSpansYears, item.month_short)
          ),
          datasets: [
            {
              label: t('dashboard.chart_sample_registrations', {
                defaultValue: 'サンプル登録数',
              }),
              data: chartPoints.map((item) => item.count),
              borderColor: '#2563eb',
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: chartLabelCount > 8 ? 3 : 5,
              pointHoverRadius: 7,
              pointBackgroundColor: '#2563eb',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              pointHoverBackgroundColor: '#1d4ed8',
              pointHoverBorderColor: '#ffffff',
            },
          ],
        }
      : null;

  /* 斜めラベルは狭幅で潰れるので横＋間引き。詳細はツールチップ */
  const chartOptions = buildSampleDashboardChartOptions({
    indexTooltip: true,
    hideTooltipColors: true,
    maxTicksLimit: registrationChartMaxTicksLimit(chartLabelCount),
  });
  /** 0件カテゴリは余白だけ増やすので除外。属名 typo は表示補正して合算 */
  const genusBars = mergePathogenGenusBars(
    (distributionData?.data?.byPathogenGenus ?? [])
      .filter((item) => (item.count ?? 0) > 0)
      .map((item) => ({ genus: item.genus, count: item.count ?? 0 }))
  ).slice(0, 10);
  const bslBars = sortBslCountBars(
    (distributionData?.data?.byBslLevel ?? []).filter((item) => (item.count ?? 0) > 0)
  );
  const countBarLabel = t('sample.count', { defaultValue: 'Count' });
  const genusHorizontal = genusBars.length > 0 && genusBars.length <= 5;
  const bslHorizontal = bslBars.length > 0 && bslBars.length <= 5;
  const genusChartOptions = buildDistributionBarChartOptions({
    animation: false,
    horizontal: genusHorizontal,
  });
  /** 件数のみ（合計数量はツールチップ） */
  const bslBaseOptions = buildDistributionBarChartOptions({
    animation: false,
    horizontal: bslHorizontal,
  });
  const bslChartOptions = {
    ...bslBaseOptions,
    plugins: {
      ...bslBaseOptions.plugins,
      tooltip: {
        ...bslBaseOptions.plugins.tooltip,
        callbacks: {
          afterBody(items: Array<{ dataIndex: number }>) {
            const idx = items[0]?.dataIndex;
            const qty = bslBars[idx]?.totalQuantity;
            if (qty == null) return [];
            return `${t('sample.total_quantity', { defaultValue: 'Total quantity' })} · ${qty}`;
          },
        },
      },
    },
  };

  const hasStorageRooms = (stats?.storage_rooms || 0) > 0;
  const hasSamples = (stats?.samples || 0) > 0;
  const showUsersCard = Boolean(adminViewAll && stats && stats.users > 0);
  const statColMd = showUsersCard ? 3 : 4;
  const showDistributionCharts = genusBars.length > 0 || bslBars.length > 0;

  return (
    <Container fluid id="sample-dashboard-page" className={`mt-2 px-3 px-md-4 pb-3 ${styles.page}`}>
      <DashboardChrome {...chromeProps} />

      <div
        className={isFetching ? styles.contentFetching : undefined}
        aria-busy={isFetching || undefined}
      >
      <div
        className={styles.statsWithAdmin}
        role="group"
        aria-label={t('dashboard.stats_scope_group', {
          defaultValue: 'Statistics and view scope',
        })}
      >
        {showAdminViewToggle ? (
          <div className={styles.statsAdminSlot}>
            <AdminStorageViewToggle
              checked={adminViewAll}
              onChange={setAdminViewAll}
              inputId="dashboard-admin-view-all"
              compact
              className="flex-shrink-0"
              labelKey="sample.admin_view_all_label"
              shortLabelKey="sample.admin_view_all_label_short"
              offShortLabelKey="sample.admin_view_scope_mine_short"
              hintKey="sample.admin_view_all_hint"
            />
          </div>
        ) : null}
        <Row className={`mb-0 g-2 ${styles.statsRow}`}>
          <Col xs={6} md={statColMd}>
            <DashboardStatCard
              /* スコープ文言は AdminStorageViewToggle 側。カードは件数ラベルのみ */
              label={t('dashboard.stats_samples', { defaultValue: 'Samples' })}
              value={stats?.samples || 0}
              iconClassName="fa-vial"
              toneClassName={styles.statCardSamples}
              to={hasStorageRooms ? '/samples?stat=total' : SAMPLE_STORAGE_SETUP_HREF}
              linkTitle={
                hasStorageRooms
                  ? t('dashboard.stats_samples_link', {
                      defaultValue: 'すべてのサンプル一覧を開く · {{count}}件',
                      count: stats?.samples || 0,
                    })
                  : t('sample.list_empty_go_to_storage', {
                      defaultValue: '保管庫を登録する',
                    })
              }
            />
          </Col>
          <Col xs={6} md={statColMd}>
            <DashboardStatCard
              label={t('dashboard.stats_storage_rooms', { defaultValue: 'Storage rooms' })}
              value={stats?.storage_rooms || 0}
              iconClassName="fa-warehouse"
              toneClassName={styles.statCardRooms}
              to={hasStorageRooms ? '/storage-rooms' : SAMPLE_STORAGE_SETUP_HREF}
              linkTitle={
                hasStorageRooms
                  ? t('dashboard.stats_storage_rooms_link', {
                      defaultValue: '保管庫管理を開く · {{count}}件',
                      count: stats?.storage_rooms || 0,
                    })
                  : t('sample.list_empty_go_to_storage', {
                      defaultValue: '保管庫を登録する',
                    })
              }
            />
          </Col>
          <Col xs={showUsersCard ? 6 : 12} md={statColMd}>
            <DashboardStatCard
              label={t('dashboard.stats_total_usage', { defaultValue: 'Total usage records' })}
              value={stats?.totalUsageRecords || 0}
              iconClassName="fa-notes-medical"
              toneClassName={styles.statCardUsage}
              to={hasStorageRooms ? '/samples/usage-records' : SAMPLE_STORAGE_SETUP_HREF}
              linkTitle={
                hasStorageRooms
                  ? t('dashboard.stats_total_usage_link', {
                      defaultValue: '使用履歴を開く · {{count}}件',
                      count: stats?.totalUsageRecords || 0,
                    })
                  : t('sample.list_empty_go_to_storage', {
                      defaultValue: '保管庫を登録する',
                    })
              }
            />
          </Col>
          {showUsersCard ? (
            <Col xs={6} md={3}>
              <DashboardStatCard
                label={t('dashboard.stats_users', { defaultValue: 'Users' })}
                value={stats!.users}
                iconClassName="fa-users"
                toneClassName={styles.statCardUsers}
              />
            </Col>
          ) : null}
        </Row>
      </div>

      <Row className="mb-3 g-3 align-items-start">
        <Col lg={useSparseRegistrationChart ? 4 : 8} md={12}>
          <Card
            className={`border-0 shadow-sm sample-panel-card ${styles.panelCard}${
              !useSparseRegistrationChart ? ' h-100' : ''
            }`}
          >
            <SamplePanelCardHeader
              icon="fa-chart-line"
              title={t('dashboard.chart_title', {
                defaultValue: 'サンプル登録トレンド',
              })}
            />
            <Card.Body className="p-2 p-md-3">
              {chartData && useSparseRegistrationChart ? (
                <div
                  className={styles.chartSparseSummary}
                  role="status"
                  aria-label={t('dashboard.chart_sparse_aria', {
                    count: sparseChartPoint?.count ?? 0,
                    defaultValue: 'サンプル登録トレンドの要約: {{count}}件',
                  })}
                >
                  <div className={styles.chartSparseValue}>
                    {sparseChartPoint?.count ?? 0}
                    <span className={styles.chartSparseUnit}>
                      {t('common.unit_items', { defaultValue: '件' })}
                    </span>
                  </div>
                  <div className={styles.chartSparseTrack} aria-hidden="true">
                    <span className={styles.chartSparseFill} />
                    <span className={styles.chartSparseDot} />
                  </div>
                  <div className={`text-muted ${styles.chartSparseMeta}`}>
                    {t('dashboard.chart_sparse_single', {
                      month:
                        sparseChartPoint?.month_short ||
                        sparseChartPoint?.month ||
                        '—',
                      defaultValue: '{{month}} のみ（点が少ないため要約）',
                    })}
                  </div>
                </div>
              ) : chartData ? (
                <ChartFigure
                  label={t('dashboard.chart_aria', {
                    defaultValue: t('dashboard.chart_title'),
                  })}
                  className={`${styles.chartWrap}${chartLabelCount <= 3 ? ` ${styles.chartWrapCompact}` : ''}`}
                >
                  <Line data={chartData} options={chartOptions as any} />
                </ChartFigure>
              ) : (
                <div className={`text-center py-4 text-muted ${styles.emptyHint}`}>
                  <div className="mb-2" aria-hidden="true">
                    <i className="fas fa-chart-area fa-2x opacity-50" />
                  </div>
                  <div>
                    {t('dashboard.chart_no_data', {
                      defaultValue: '表示できる登録データがまだありません',
                    })}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={useSparseRegistrationChart ? 8 : 4} md={12}>
          <Card className={`border-0 shadow-sm sample-panel-card ${styles.panelCard}${!useSparseRegistrationChart ? ' h-100' : ''}`}>
            <SamplePanelCardHeader
              icon="fa-clock"
              title={t('dashboard.recent_activity', { defaultValue: 'Recent activity' })}
              trailing={
                <Badge bg="secondary" className={styles.activityBadge}>
                  {t('dashboard.latest_n', {
                    count: DASHBOARD_ACTIVITY_PREVIEW,
                    defaultValue: 'Latest {{count}}',
                  })}
                </Badge>
              }
            />
            <Card.Body className={styles.activityBody}>
              {stats?.activity_data && stats.activity_data.length > 0 ? (
                <ListGroup variant="flush">
                  {stats.activity_data.slice(0, DASHBOARD_ACTIVITY_PREVIEW).map((activity) => {
                    const formatted = formatDashboardActivity(activity, t, i18n.language);
                    const sampleId = activity.sample_id;
                    const canOpen =
                      Number.isInteger(sampleId) && sampleId >= 1;
                    // 同名サンプルの使用/登録が並ぶため aria に種別を付ける。詳細は title。
                    const sampleLabel = activity.sample_name
                      ? formatEntityDisplayName(activity.sample_name, t)
                      : t('sample.open_detail_page_short', {
                          defaultValue: t('sample.open_detail_page'),
                        });
                    const openAria = t('dashboard.activity_open_aria', {
                      kind: formatted.kindLabel,
                      name: sampleLabel,
                      defaultValue: `${formatted.kindLabel} · ${sampleLabel}`,
                    });
                    const whenWho = activity.creator_name
                      ? joinLabeledValue(
                          joinLabeledValue(formatted.dateLabel, formatted.dateTimeDisplay),
                          activity.creator_name
                        )
                      : joinLabeledValue(formatted.dateLabel, formatted.dateTimeDisplay);
                    const creatorHint = joinLabeledValue(formatted.text, whenWho);

                    return (
                      <ListGroup.Item
                        key={activity.id}
                        className={styles.activityItem}
                        action={canOpen}
                        as={canOpen ? 'button' : 'div'}
                        {...(canOpen ? { type: 'button' as const } : {})}
                        aria-label={canOpen ? openAria : undefined}
                        title={creatorHint}
                        onClick={
                          canOpen
                            ? () =>
                                navigateToSampleDetail(navigate, sampleId, {
                                  preferAdminViewAll: adminViewAll,
                                  returnPath: '/samples/dashboard',
                                })
                            : undefined
                        }
                      >
                        <div className={styles.activityRow}>
                          <div className={styles.activityMain}>
                            {/* 種別・時刻・場所（ID は title）。本文は名前＋動詞（＋フリーノート） */}
                            <div className={styles.activityMeta}>
                              <Badge
                                bg={formatted.badgeColor}
                                className={styles.activityKind}
                              >
                                {formatted.kindLabel}
                              </Badge>
                              <time
                                className={styles.activityTime}
                                dateTime={activity.activity_date}
                              >
                                {' · '}
                                {formatted.dateTimeDisplay}
                              </time>
                              {formatted.sampleRefLine ? (
                                <span
                                  className={`${styles.activityRefInline} sample-ellipsis-show-tail`}
                                  title={formatted.sampleRefTitle || formatted.sampleRefLine}
                                >
                                  {' · '}
                                  <span className="sample-ellipsis-show-tail-inner">
                                    {softBreakIdentifier(formatted.sampleRefLine, t)}
                                  </span>
                                </span>
                              ) : null}
                            </div>
                            <div className={styles.activityText}>
                              <span className={styles.activityLead}>
                                {softBreakIdentifier(formatted.sampleName, t)}
                                {formatted.headlineSuffix}
                              </span>
                              {formatted.noteLine ? (
                                <span
                                  className={`${styles.activityNote} sample-ellipsis-show-tail`}
                                  title={formatted.noteLine}
                                >
                                  {' · '}
                                  <span className="sample-ellipsis-show-tail-inner">
                                    {softBreakIdentifier(formatted.noteLine, t)}
                                  </span>
                                </span>
                              ) : null}
                            </div>
                          </div>
                          {activity.creator_name ? (
                            <span className={styles.activityCreator}>
                              {' · '}
                              {activity.creator_name}
                            </span>
                          ) : null}
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              ) : (
                <div className={`p-4 text-center ${styles.activityEmpty}`}>
                  <div className="mb-2" aria-hidden="true">
                    <i className="fas fa-inbox fa-2x opacity-50" />
                  </div>
                  <div className={styles.emptyHint}>
                    {t('dashboard.no_recent_activity', { defaultValue: 'No recent activity' })}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showDistributionCharts ? (
        <Row className="mb-3 g-3">
          {genusBars.length > 0 ? (
            <Col md={bslBars.length > 0 ? 6 : 12}>
              <DistributionBarCard
                title={t('sample.distribution_by_genus', {
                  defaultValue: 'Distribution by pathogen genus',
                })}
                icon="fa-virus"
                labels={genusBars.map((item) => item.genus)}
                values={genusBars.map((item) => item.count)}
                countLabel={countBarLabel}
                options={genusChartOptions as Record<string, unknown>}
                chartKey={`pathogen-genus-${genusBars.length}-${genusBars[0]?.genus ?? ''}`}
              />
            </Col>
          ) : null}

          {bslBars.length > 0 ? (
            <Col md={genusBars.length > 0 ? 6 : 12}>
              <DistributionBarCard
                title={t('sample.distribution_by_bsl', {
                  defaultValue: 'Distribution by BSL level',
                })}
                icon="fa-shield-alt"
                labels={bslBars.map((item) => item.level)}
                values={bslBars.map((item) => item.count)}
                countLabel={countBarLabel}
                options={bslChartOptions as Record<string, unknown>}
                chartKey={`bsl-level-${bslBars.length}-${bslBars[0]?.level ?? ''}`}
              />
            </Col>
          ) : null}
        </Row>
      ) : null}

      <SampleDashboardQuickAccess
        hasStorageRooms={hasStorageRooms}
        hasSamples={hasSamples}
      />
      </div>
    </Container>
  );
}
